import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getClubNews, getClub } from '../../services/clubService';
import { ClubNews, Club } from '../../types/clubs';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { CalendarIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { OptimizedImage } from '../../components/common/OptimizedImage';
import { getCardImageUrl } from '../../utils/imageOptimization';

const ClubNewsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const [club, setClub] = useState<Club | null>(null);
  const [news, setNews] = useState<ClubNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (clubId) {
      loadClubInfo();
      loadNews();
    }
  }, [clubId]);

  const loadClubInfo = async () => {
    if (!clubId) return;
    
    try {
      const clubData = await getClub(clubId);
      setClub(clubData);
    } catch (err: any) {
      console.error('Error fetching club info:', err);
    }
  };

  const loadNews = async () => {
    if (!clubId) return;
    
    try {
      setError(null);
      const fetchedNews = await getClubNews(clubId);
      setNews(fetchedNews);
    } catch (err: any) {
      console.error('Error fetching club news:', err);
      const errorMessage = err.message || t('common.error') || 'Failed to load club news';
      setError(errorMessage);
      
      // Check if error is due to not being a member
      if (err.response?.status === 403 || err.message?.includes('member')) {
        setError(t('clubs.memberOnlyContent') || 'This content is only available to club members');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading && news.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">{t('common.loading') || 'Loading club news...'}</div>
      </div>
    );
  }

  if (error && news.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          {error.includes('member') && clubId && (
            <Link
              to={`/clubs/${clubId}`}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {t('clubs.viewClub') || 'View Club'}
            </Link>
          )}
          {!error.includes('member') && (
            <button
              onClick={loadNews}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {t('common.retry') || 'Retry'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {club && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{club.name}</h1>
          <p className="text-gray-600">{t('clubs.news') || 'News & Updates'}</p>
        </div>
      )}

      {news.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">{t('clubs.noNews') || 'No news available'}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {news.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {item.imageUrl && (
                <OptimizedImage
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-64"
                  optimization={{ width: 800, quality: 75 }}
                  loading="lazy"
                  placeholder="skeleton"
                />
              )}
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h2>
                <div className="flex items-center text-sm text-gray-500 mb-4">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  <span>{formatDate(item.createdAt)}</span>
                  {item.isMemberOnly && (
                    <>
                      <LockClosedIcon className="h-4 w-4 ml-4 mr-2 text-primary-600" />
                      <span className="text-primary-600">{t('clubs.memberOnly') || 'Member Only'}</span>
                    </>
                  )}
                </div>
                <div className="text-gray-700 whitespace-pre-wrap">{item.content}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClubNewsPage;
